import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CategoryFilterBar from './CategoryFilterBar';
import { useParams } from 'react-router-dom';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import PriceItem from './PriceItem';
import NewVarietyModal from '../components/NewVarietyModal';
import ProductCreationModal from "../components/ProductCreationModal";
import { getProducts, deleteProduct, addVarietyToProduct, updateProduct, deleteVariety, markAsAvailable, markAsSold, getLastStockItemForEachVariety, getLastStockItemsAfterPriceUpdate, uploadFile, getCategories, getBranchTaxes } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from './InlineLoading';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';

const ProductList = ({
  branchId: branchIdProp,
  embedded = false,
  canReadProducts = true,
  canCreateProduct = true,
  canUpdateProduct = true,
  canDeleteProduct = true,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [productNameInput, setProductNameInput] = useState('');
  const [productImageInput, setProductImageInput] = useState('');
  const [productImageUrl, setProductImageUrl] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [barcodeScopeInput, setBarcodeScopeInput] = useState('global');
  const [isLoading, setIsLoading] = useState(false);
	  const [products, setProducts] = useState([]);
	  const [stockItems, setStockItems] = useState([]);
	  const [search, setSearch] = useState('');
	  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesError, setTaxesError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
	  const [varietiesMissingUpdate, setPricesMissingUpdate] = useState([]);
	  const [showOnlyMissingUpdate, setShowOnlyMissingUpdate] = useState(false); // ✅ New state
	const [showAddVarietyModal, setShowAddVarietyModal] = useState(false);
	const params = useParams();
	const branchId = branchIdProp ?? params.branchId ?? localStorage.getItem('selectedBranchId');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchVarietyResults, setSearchVarietyResults] = useState([]);
  const [useDefaultTaxesInput, setUseDefaultTaxesInput] = useState(true);
  const [taxIdsInput, setTaxIdsInput] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const canReadTaxes = hasPermission(user, 'tax.read');
  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) return;
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    if (!selectedProduct) return;
    const updated = products.find((product) => product.id === selectedProduct.id);
    if (updated) {
      setSelectedProduct(updated);
    } else {
      setSelectedProduct(null);
    }
  }, [products, selectedProduct?.id]);
  const categoryIdByName = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((category) => {
      const name = category?.name ? String(category.name).trim().toLowerCase() : '';
      if (name) {
        map.set(name, category.id);
      }
    });
    return map;
  }, [categories]);
  const resolveProductCategoryId = useCallback((product) => {
    if (Number.isInteger(product?.categoryId)) return product.categoryId;
    const name = product?.category ? String(product.category).trim().toLowerCase() : '';
    if (!name) return null;
    return categoryIdByName.get(name) || null;
  }, [categoryIdByName]);
  const categoriesForDisplay = useMemo(
    () => (categories || [])
      .slice()
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' }))
      .map((category) => ({
        ...category,
        label: category?.isActive === false ? `${category.name} (inactiva)` : category?.name,
      })),
    [categories]
  );
  const categoriesToRender = useMemo(() => {
    if (selectedCategory === 'all' || search) return categoriesForDisplay;
    return categoriesForDisplay.filter(
      (category) => String(category.id) === String(selectedCategory)
    );
  }, [categoriesForDisplay, search, selectedCategory]);

  useEffect(() => {
    if (selectedProduct) {
      setProductNameInput(selectedProduct.productName);
      const resolvedCategoryId = resolveProductCategoryId(selectedProduct);
      setCategoryInput(resolvedCategoryId ? String(resolvedCategoryId) : '');
      setProductImageUrl(selectedProduct.productImage);
      setBarcodeScopeInput(selectedProduct.barcodeScope || 'global');
      setUseDefaultTaxesInput(selectedProduct.useDefaultTaxes !== false);
      setTaxIdsInput(Array.isArray(selectedProduct.taxIds) ? selectedProduct.taxIds : []);
    }
  }, [selectedProduct, resolveProductCategoryId]);

	  const mergeProducts = (current, incoming) => {
	    const existingIds = new Set(current.map((product) => product.id));
	    const next = [...current];
	    (incoming || []).forEach((product) => {
	      if (!existingIds.has(product.id)) {
	        existingIds.add(product.id);
	        next.push(product);
	      }
	    });
	    return next;
	  };

	  const loadProducts = useCallback(async (nextPage = 1, append = false) => {
	    if (!canReadProducts || !branchId) return;
	    if (append) {
	      setLoadingMore(true);
	    } else {
	      setLoading(true);
	    }
	    setError(null);
	    try {
	      const data = await getProducts(branchId, {
          page: nextPage,
          pageSize,
          sortBy: 'categoryOrder',
          sortOrder: 'asc',
        });
	      const normalizedProducts = normalizePaginatedResponse(data);
	      setProducts((prev) =>
	        append ? mergeProducts(prev, normalizedProducts.data || []) : (normalizedProducts.data || [])
	      );
	      setHasNext(Boolean(normalizedProducts.hasNext));
	      setPage(nextPage);
	      if (!append) {
	        const stockItems = await getLastStockItemForEachVariety(branchId, { pageSize: 0 });
	        const normalizedStockItems = normalizePaginatedResponse(stockItems);
	        setStockItems(normalizedStockItems.data || []);
	        const missingUpdate = await getLastStockItemsAfterPriceUpdate(branchId, { pageSize: 0 });
	        const normalizedMissing = normalizePaginatedResponse(missingUpdate);
	        setPricesMissingUpdate(normalizedMissing.data || []);
	      }
	    } catch (err) {
	      console.error('Error fetching prices:', err);
	      setError('Failed to load prices.');
	    } finally {
	      if (append) {
	        setLoadingMore(false);
	      } else {
	        setLoading(false);
	      }
	    }
	  }, [branchId, canReadProducts, pageSize]);

	  useEffect(() => {
	    if (!canReadProducts) {
	      setLoading(false);
	      setError('No tienes permisos para ver productos.');
	      return;
	    }
      if (!branchId) {
        setLoading(false);
        setError('Selecciona una sucursal para ver productos.');
        return;
      }
      setProducts([]);
      setPage(1);
      setHasNext(false);
	    loadProducts(1, false);
	  }, [branchId, canReadProducts, loadProducts]);

	  useEffect(() => {
	    const sentinel = loadMoreRef.current;
	    if (!sentinel) return;
	    const observer = new IntersectionObserver(
	      (entries) => {
	        if (!entries[0].isIntersecting) return;
	        if (!hasNext || loadingMore || loading) return;
	        loadProducts(page + 1, true);
	      },
	      { rootMargin: '200px' }
	    );
	    observer.observe(sentinel);
	    return () => observer.disconnect();
	  }, [hasNext, loadingMore, loading, loadProducts, page]);

  useEffect(() => {
    if (!branchId) return;
    setCategoriesLoading(true);
    setCategoriesError(null);
    getCategories({ includeAvailability: true, branchId })
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
        setCategories([]);
        setCategoriesError('No se pudieron cargar las categorías.');
      })
      .finally(() => setCategoriesLoading(false));
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return;
    if (!canReadTaxes) {
      setTaxes([]);
      setTaxesError('No tienes permisos para ver impuestos.');
      return;
    }
    setTaxesLoading(true);
    setTaxesError(null);
    getBranchTaxes(branchId)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const active = list.filter((tax) => tax.branchActive && tax.isActive !== false);
        setTaxes(active);
      })
      .catch((err) => {
        console.error('Error fetching taxes:', err);
        setTaxes([]);
        setTaxesError('No se pudieron cargar los impuestos.');
      })
      .finally(() => setTaxesLoading(false));
  }, [branchId, canReadTaxes]);

  useEffect(() => {
    if (selectedCategory === 'all') return;
    const isStillValid = categoriesForDisplay.some(
      (category) => String(category.id) === String(selectedCategory)
    );
    if (!isStillValid) {
      setSelectedCategory('all');
    }
  }, [categoriesForDisplay, selectedCategory]);

  const handleUpdateProduct = async (id) => {
    if (!canUpdateProduct) return;
    setIsLoading(true);
    try {
      const selectedCategoryData = categories.find(
        (category) => String(category.id) === String(categoryInput)
      );
      const data = {
        productName: productNameInput,
        category: selectedCategoryData?.name,
        categoryId: selectedCategoryData?.id,
        barcodeScope: barcodeScopeInput,
        useDefaultTaxes: useDefaultTaxesInput,
        taxIds: useDefaultTaxesInput ? [] : taxIdsInput,
      }
      if (productImageInput) {
        const imageUrl = await uploadFile(productImageInput);
        data.productImage = imageUrl.url;
      }
      const updated = await updateProduct(branchId, id, data);
      const nextProduct = updated?.id ? updated : { id, ...data };
      setProducts(products.map((product) => (product.id === id ? { ...product, ...nextProduct } : product)));
      if (selectedProduct && selectedProduct.id === id) {
        setSelectedProduct({ ...selectedProduct, ...nextProduct });
      }
      setShowEdit(false);
      setIsLoading(false);
    } catch (error) {
      console.error('Error updating product:', error.message);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!canDeleteProduct) return;
    setIsLoading(true);
    try {
      const approved = await confirm({
        title: 'Eliminar producto',
        message: '¿Estás seguro de que deseas eliminar este producto?',
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      });
      if (!approved) return;
      await deleteProduct(branchId, id);
      setProducts(products.filter((product) => product.id !== id));
      if (selectedProduct && selectedProduct.id === id) {
        setSelectedProduct(null);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error deleting product:', error.message);
    }
  };

  const handleAddVariety = async (variety) => {
    try {
      if (!canUpdateProduct) return;
      setProducts(products.map((product) => (product.id === selectedProduct.id ? { ...product, Varieties: [...product.Varieties, variety] } : product)));
      setSelectedProduct({ ...selectedProduct, Varieties: [...selectedProduct.Varieties, variety] });
    } catch (error) {
      console.error('Error adding variety:', error.message);
    }
  };

  const handleUpdateVariety = async (idOrVariety, data) => {
    try {
      if (!canUpdateProduct) return;
      const varietyId = typeof idOrVariety === 'object' ? idOrVariety.id : idOrVariety;
      const updateData = typeof idOrVariety === 'object' ? idOrVariety : data;
      if (!varietyId) return;
      setProducts(products.map((product) => {
        const updatedVarieties = product.Varieties.map((variety) => (variety.id === varietyId ? { ...variety, ...updateData } : variety));
        return { ...product, Varieties: updatedVarieties };
      }));
      if (selectedProduct) {
        setSelectedProduct({ ...selectedProduct, Varieties: selectedProduct.Varieties.map((variety) => (variety.id === varietyId ? { ...variety, ...updateData } : variety)) });
      }
    } catch (error) {
      console.error('Error updating variety:', error.message);
    }
  };

  const handleVarietyUpdateSuccess = () => {
    setSelectedProduct(null);
    if (search) {
      setSearch('');
      setSearchVarietyResults([]);
    }
  };

	  // ✅ Updated filter function
	  const filterProducts = (searchValue, category) => {
	    let filtered = products;
	    let varietyResults = [];
	    const normalizedSearch = (searchValue || '').trim().toLowerCase();
	
	    if (normalizedSearch) {
	      // Search ignoring category, match only words starting with the search
	      filtered = products.filter((product) =>
	        product.productName
	          .toLowerCase()
	          .split(' ')
	          .some((word) => word.startsWith(normalizedSearch))
	      );

	      // Also search in variety names
	      varietyResults = products.flatMap((product) =>
	        (product.Varieties || [])
	          .filter(
	            (variety) =>
	              variety.varietyName &&
	              variety.varietyName.toLowerCase().includes(normalizedSearch)
	          )
	          .map((variety) => ({ product, variety }))
	      );
	    } else if (category !== 'all') {
	      // Only filter by category if search is empty
	      filtered = products.filter(
          (product) => String(resolveProductCategoryId(product)) === String(category)
        );
	    }
	
	    if (showOnlyMissingUpdate) {
	      filtered = filtered.filter((product) =>
	        varietiesMissingUpdate.some((item) =>
	          product.Varieties?.some((variety) => variety.id === item.varietyId)
	        )
	      );

	      if (normalizedSearch) {
	        varietyResults = varietyResults.filter(({ variety }) =>
	          varietiesMissingUpdate.some((item) => item.varietyId === variety.id)
	        );
	      }
	    }
	
	    setFilteredProducts(filtered);
	    setSearchVarietyResults(varietyResults);
	  };

	  useEffect(() => {
	    filterProducts(search, selectedCategory);
	  }, [search, selectedCategory, products, showOnlyMissingUpdate, varietiesMissingUpdate]);

  const handleDeleteVariety = async (id) => {
    try {
      if (!canUpdateProduct) return;
      const approved = await confirm({
        title: 'Eliminar variedad',
        message: '¿Estás seguro de que deseas eliminar este producto?',
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      });
      if (!approved) return;
      const response = await deleteVariety(branchId, id);
      setProducts(products.map((product) => {
        const updatedVarieties = product.Varieties.filter((variety) => variety.id !== id);
        return { ...product, Varieties: updatedVarieties };
      }));
      if (selectedProduct) {
        setSelectedProduct({ ...selectedProduct, Varieties: selectedProduct.Varieties.filter((variety) => variety.id !== id) });
      }
    } catch (error) {
      console.error('Error deleting product:', error.message);
    }
  };

  const handleMarkAsSoldOut = async (id) => {
    try {
      if (!canUpdateProduct) return;
      await markAsSold(branchId, id);
      setProducts(products.map((product) => {
        const updatedVarieties = product.Varieties.map((variety) => (variety.id === id ? { ...variety, isSoldOut: true } : variety));
        return { ...product, Varieties: updatedVarieties };
      }));
      if (selectedProduct) {
        setSelectedProduct({ ...selectedProduct, Varieties: selectedProduct.Varieties.map((variety) => (variety.id === id ? { ...variety, isSoldOut: true } : variety)) });
      }
    } catch (error) {
      console.error('Error marking product as sold out:', error.message);
    }
  };

  const handleMarkAsAvailable = async (id) => {
    try {
      if (!canUpdateProduct) return;
      await markAsAvailable(branchId, id);
      setProducts(products.map((product) => {
        const updatedVarieties = product.Varieties.map((variety) => (variety.id === id ? { ...variety, isSoldOut: false } : variety));
        return { ...product, Varieties: updatedVarieties };
      }));
      if (selectedProduct) {
        setSelectedProduct({ ...selectedProduct, Varieties: selectedProduct.Varieties.map((variety) => (variety.id === id ? { ...variety, isSoldOut: false } : variety)) });
      }
    } catch (error) {
      console.error('Error marking product as available:', error.message);
    }
  };

  if (loading) {
    return (
      <p>
        <InlineLoading />
      </p>
    );
  }
  if (error) return <p>{error}</p>;

  return (
    <>
      {isModalOpen && (
        <ProductCreationModal
          onClose={setIsModalOpen}
          canCreateProduct={canCreateProduct}
          categories={categoriesForDisplay}
          taxes={taxes}
        />
      )}
      <NewVarietyModal
        isOpen={showAddVarietyModal}
        product={selectedProduct}
        existingVarieties={selectedProduct?.Varieties || []}
        barcodeScope={selectedProduct?.barcodeScope}
        onClose={() => { setShowAddVarietyModal(false); }}
        onVarietyCreated={handleAddVariety}
        canUpdateProduct={canUpdateProduct}
      />

      <div className={embedded ? "" : "p-4"}>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold">Productos</h1>
          {canCreateProduct && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 text-sm"
            >
              + Producto Categoría
            </button>
          )}
        </div>
        {/* ✅ Search and Missing Update Filter */}
        <div className="sticky top-14 pt-2 bg-white z-10 border-b mt-4 pb-2">
          <input
            type="text"
            placeholder="Buscar producto..."
            className="border border-gray-300 rounded p-2 mb-2 w-full"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="missingUpdate"
              checked={showOnlyMissingUpdate}
              onChange={(e) => setShowOnlyMissingUpdate(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="missingUpdate" className="text-sm">Mostrar solo productos por actualizar</label>
          </div>
        </div>

        <CategoryFilterBar
          categories={categoriesForDisplay}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
          includeAll
          allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
          loading={categoriesLoading}
          loadingMessage={<InlineLoading label="Cargando categorías" />}
          error={categoriesError}
          emptyMessage="No hay categorías disponibles."
          isEmpty={categoriesForDisplay.length === 0}
          itemsWrapperClassName="mb-6"
        />

	        {/* Search Results Category */}
	        {search && searchVarietyResults.length > 0 && (
	          <div className="space-y-4 mb-6">
	            <div className="flex items-center gap-4 my-6">
	              <div className="flex-grow border-t border-gray-300"></div>
	              <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
	                Resultados de Búsqueda
	              </h2>
	              <div className="flex-grow border-t border-gray-300"></div>
	            </div>

	            <div className="grid grid-cols-3 gap-1 md:grid-cols-4 lg:grid-cols-6">
	              {searchVarietyResults.map(({ product, variety }) => (
	                <div
	                  key={`${product.id}-${variety.id}`}
	                  className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition-shadow flex flex-col justify-between cursor-pointer"
	                  onClick={() => setSelectedProduct(product)}
	                >
	                  <div className="relative w-full" style={{ paddingBottom: '100%' }}>
	                    <img
	                      src={variety.varietyImage || product.productImage || 'https://placehold.co/200'}
	                      alt={variety.varietyName || product.productName}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
	                      className="absolute inset-0 w-full h-full object-cover rounded"
	                    />
	                  </div>
	                  <h3 className="text-sm font-semibold break-words mt-2">
	                    {product.productName}
	                    {variety.varietyName && (
	                      <>
	                        <br />
	                        {variety.varietyName}
	                      </>
	                    )}
	                  </h3>
	                </div>
	              ))}
	            </div>
	          </div>
	        )}

        {/* Filtered Prices */}
        <div className="space-y-8">
          {categoriesToRender.map((category) => {
            const productsInCategory = filteredProducts
              .filter((p) => resolveProductCategoryId(p) === category.id)
              .sort((a, b) => a.productName.localeCompare(b.productName)); // Alphabetical

            if (productsInCategory.length === 0) return null;

            return (
              <div key={category.id}>
                {/* Category Title */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                    {category.name}
                  </h2>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-3 gap-1 md:grid-cols-4 lg:grid-cols-6">
                  {productsInCategory.map((product) => (
                    <div
                      key={product.id}
                      className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition-shadow flex flex-col justify-between cursor-pointer"
                      onClick={() => setSelectedProduct(product)}
                    >
                      <div className="relative w-full" style={{ paddingBottom: '100%' }}>
	                      <img
	                        src={product.productImage || 'https://placehold.co/200'}
	                        alt={product.productName}
                          loading="lazy"
                          decoding="async"
                          fetchPriority="low"
	                        className="absolute inset-0 w-full h-full object-cover rounded"
	                      />
	                    </div>
                      <h3 className="text-sm font-semibold break-words mt-2">{product.productName}<br />[ver detalle]</h3>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {hasNext && (
          <div ref={loadMoreRef} className="h-10 flex items-center justify-center text-sm text-gray-500 mt-4">
            {loadingMore ? (
              <InlineLoading label="Cargando más productos" />
            ) : (
              "Desplázate para cargar más"
            )}
          </div>
        )}
        {!hasNext && !loading && products.length > 0 && (
          <div className="text-sm text-gray-400 text-center mt-4">
            Ya se cargaron todos los productos.
          </div>
        )}
        {/* Modal for Varieties */}
        {selectedProduct && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40"
            onClick={() => setSelectedProduct(null)}
          >
            <div
              className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4 relative flex flex-col"
              style={{ maxHeight: "80vh", display: "flex" }} // Ensures the modal does not exceed 80% of the screen height
              onClick={(e) => e.stopPropagation()}
            >
              {/* Scrollable Content */}
              <div className="overflow-y-auto px-2" style={{ maxHeight: "70vh" }}>
                {/* Close Button */}
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute top-2 right-2 text-gray-600 hover:text-black bg-white p-1 rounded-full"
                >
                  ✕
                </button>

                {/* Centered Image with Increased Size */}
                <div className="flex justify-center items-center mb-4">
                  <img
                    src={productImageUrl || selectedProduct.productImage || "https://placehold.co/300"}
                    alt={selectedProduct.productName}
                    className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-lg"
                  />
                </div>

                {/* Image Upload */}
                {showEdit && (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Imagen del Producto:</label>
                    <input
                      type="file"
                      id="productImage"
                      name="productImage"
                      onChange={(e) => {
                        setProductImageInput(e.target.files[0]);
                        setProductImageUrl(URL.createObjectURL(e.target.files[0]));
                      }}
                      accept="image/*"
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    />
                  </div>
                )}

                {/* Product Name */}
                {showEdit ? (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Nombre del Producto:</label>
                    <input
                      type="text"
                      id="productName"
                      name="productName"
                      value={productNameInput}
                      onChange={(e) => setProductNameInput(e.target.value)}
                      required
                      autoComplete="off"
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    />
                  </div>
                ) : (
                  <h2 className="text-xl font-bold mb-2 text-center">{selectedProduct.productName}</h2>
                )}

                {/* Category Selection */}
                {showEdit ? (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Categoría:</label>
                    <select
                      id="category"
                      name="category"
                      required
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="">Selecciona una categoría</option>
                      {categoriesForDisplay.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="text-md mb-2 text-center">
                    <strong>Categoría:</strong>{" "}
                    {(() => {
                      const resolvedId = resolveProductCategoryId(selectedProduct);
                      const resolvedCategory = categoriesForDisplay.find(
                        (category) => category.id === resolvedId
                      );
                      return resolvedCategory?.name || selectedProduct.category || 'Sin categoría';
                    })()}
                  </p>
                )}

                {/* Barcode Scope */}
                {showEdit ? (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Alcance del código de barras:</label>
                    <select
                      id="barcodeScope"
                      name="barcodeScope"
                      value={barcodeScopeInput}
                      onChange={(e) => setBarcodeScopeInput(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="global">Global</option>
                      <option value="per_variety">Por variedad</option>
                    </select>
                  </div>
                ) : (
                  <p className="text-md mb-2 text-center">
                    <strong>Barcode:</strong>{" "}
                    {selectedProduct.barcodeScope === "per_variety" ? "Por variedad" : "Global"}
                  </p>
                )}

                {showEdit && (
                  <div className="w-80 flex flex-col mb-3">
                    <label className="block text-left font-medium">Impuestos:</label>
                    {!canReadTaxes && (
                      <span className="text-xs text-gray-500">No tienes permisos para ver impuestos.</span>
                    )}
                    {canReadTaxes && taxesLoading && (
                      <InlineLoading label="Cargando impuestos" />
                    )}
                    {canReadTaxes && !taxesLoading && taxesError && (
                      <span className="text-xs text-red-600">{taxesError}</span>
                    )}
                    {canReadTaxes && !taxesLoading && (
                      <>
                        <label className="flex items-center gap-2 text-sm text-gray-700 mt-2">
                          <input
                            type="checkbox"
                            checked={useDefaultTaxesInput}
                            onChange={(e) => setUseDefaultTaxesInput(e.target.checked)}
                          />
                          Usar impuestos por defecto (IVA)
                        </label>
                        {!useDefaultTaxesInput && (
                          <div className="flex flex-wrap gap-3 mt-2">
                            {taxes.length === 0 && (
                              <span className="text-xs text-gray-500">No hay impuestos activos.</span>
                            )}
                            {taxes.map((tax) => (
                              <label key={tax.id} className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={taxIdsInput.includes(tax.id)}
                                  onChange={(e) => {
                                    const next = new Set(taxIdsInput);
                                    if (e.target.checked) {
                                      next.add(tax.id);
                                    } else {
                                      next.delete(tax.id);
                                    }
                                    setTaxIdsInput(Array.from(next));
                                  }}
                                />
                                {tax.name} ({Number(tax.rate || 0)}%)
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between items-center w-full pt-4 mb-2">
                  {!showEdit && canUpdateProduct && (
                    <button
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                      onClick={() => setShowAddVarietyModal(true)}
                      disabled={showEdit}
                    >
                      + Agregar Tipo
                    </button>
                  )}

                  <div className="flex gap-2 ml-auto">
                    {showEdit ? (
                      <div className="flex gap-2">
                        <button
                          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                          onClick={() => handleUpdateProduct(selectedProduct.id)}
                          disabled={isLoading}
                        >
                          Guardar
                        </button>
                        <button
                          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                          onClick={() => setShowEdit(false)}
                          disabled={isLoading}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <>
                        {canUpdateProduct && (
                          <button
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                            onClick={() => setShowEdit(!showEdit)}
                            disabled={showEdit || isLoading}
                          >
                            <FiEdit size={20} />
                          </button>
                        )}
                        {canDeleteProduct && (
                          <button
                            className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 flex justify-center"
                            onClick={() => handleDeleteProduct(selectedProduct.id)}
                            disabled={showEdit || isLoading}
                          >
                            <FiTrash2 size={20} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Varieties Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!showEdit &&
                    selectedProduct?.Varieties?.map((variety) => (
                      <PriceItem
                        key={variety.id}
                        id={variety.id}
                        name={variety.varietyName}
                        productImage={variety.varietyImage}
                        prices={variety.VarietyPrices || []}
                        description={variety.description}
                        priceAutoRuleOverride={variety.priceAutoRuleOverride || null}
                        barcodeScope={selectedProduct?.barcodeScope}
                        allVarieties={selectedProduct?.Varieties || []}
                        lastStockItem={stockItems.find((item) => item.varietyId === variety.id)}
                        missingUpdate={varietiesMissingUpdate.some((item) => item.varietyId === variety.id)}
                        onDeleteVariety={handleDeleteVariety}
                        onVarietyUpdate={handleUpdateVariety}
                        onUpdateSuccess={handleVarietyUpdateSuccess}
                        canUpdateProduct={canUpdateProduct}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductList;
